import { useEffect } from "react";

import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import useVehicleComplexityRules from "../../data/useVehicleComplexityRules";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import Modal from "../../layouts/Modal";
import {
  COMPLEXITY_LABELS,
  IVehicleComplexityRule,
  VehicleComplexity,
} from "../../models/VehicleComplexity";
import { MakeCombobox } from "../parts/MakeCombobox";
import { ModelCombobox } from "../parts/ModelCombobox";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import SelectInput from "../shared/Inputs/SelectInput";
import { TextInput } from "../shared/Inputs/TextInput";

type FormValues = {
  make: string;
  model: string;
  complexity: string;
  notes: string;
};

export default function VehicleComplexityRuleModal({
  open,
  setOpen,
  rule,
  prefill,
}: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  /** Present when editing one of our own rules. */
  rule?: IVehicleComplexityRule;
  /** Make and model to start from, when adding a row another site already has. */
  prefill?: { make: string | null; model: string | null };
}) {
  const { t } = useTranslation("vehicles");
  const { createRule, updateRule } = useVehicleComplexityRules();
  const { saveHandlers } = useDefaultCRUDHandlers(t("Complexity rule"));

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<FormValues>();

  const watchMake = watch("make");
  const watchModel = watch("model");

  useEffect(() => {
    reset({
      make: rule?.make ?? prefill?.make ?? "",
      model: rule?.model ?? prefill?.model ?? "",
      complexity: String(rule?.complexity ?? VehicleComplexity.Medium),
      notes: rule?.notes ?? "",
    });
  }, [rule, prefill, open, reset]);

  const onSubmit = (data: FormValues) => {
    // Betty enforces this too, so the check here is only to save a round trip and put the message next
    // to the field rather than in a toast.
    if (!data.make?.trim()) {
      setError("make", { message: t("Choose a make") });
      return;
    }

    const body = {
      make: data.make.trim(),
      // Blank model is meaningful: it means "any model of this make". The API normalises it to null so
      // there is only one representation in the database.
      model: data.model?.trim() || null,
      complexity: Number(data.complexity) as VehicleComplexity,
      notes: data.notes?.trim() || null,
    };

    const onSuccess = () => {
      saveHandlers.onSuccess();
      setOpen(false);
    };

    if (rule) {
      updateRule.mutate({ ruleId: rule.id, body }, { onSuccess, onError: saveHandlers.onError });
      return;
    }

    createRule.mutate(body, { onSuccess, onError: saveHandlers.onError });
  };

  const complexityOptions = [
    VehicleComplexity.Easy,
    VehicleComplexity.Medium,
    VehicleComplexity.Hard,
    VehicleComplexity.Unranked,
  ].map((value) => ({ id: String(value), name: t(COMPLEXITY_LABELS[value]) }));

  return (
    <Modal open={open} setOpen={setOpen} width="max-w-2xl">
      <h1 className="text-xl">{rule ? t("Edit complexity rule") : t("Add complexity rule")}</h1>
      <p className="text-sm text-gray-500">
        {t("Pick a make. Leave the model blank to cover every model of that make — a rule naming both beats one naming only the make.")}
      </p>

      <form className="mt-4 space-y-4 border-t border-gray-200 pt-4" onSubmit={handleSubmit(onSubmit)}>
        {/*
          The same make/model comboboxes the stock and unlisted filters use, off the shared
          /utilities/lookup makes and models. That matters beyond consistency: rules are matched
          against Vehicle.CombinedMake, which comes from the same lookup, so picking from the list
          means a rule can actually match a vehicle. Free text could not promise that.

          Registered as hidden inputs because the comboboxes report a selection through a callback
          rather than being form controls, so react-hook-form needs somewhere to hold the value.
        */}
        <input type="hidden" {...register("make")} />
        <input type="hidden" {...register("model")} />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <MakeCombobox
              label={t("Make") + " *"}
              currentValue={watchMake}
              onMakeSelect={(make) => {
                setValue("make", make?.make ?? "");
                // Models are make-scoped, so a make change invalidates the model - same behaviour as
                // VehiclePartSearchFilters.
                setValue("model", "");
                clearErrors("make");
              }}
            />
            {errors.make && <p className="mt-1 text-xs text-red-600">{errors.make.message}</p>}
          </div>

          <div>
            <ModelCombobox
              label={t("Model")}
              make={watchMake}
              currentValue={watchModel}
              onModelSelect={(model) => setValue("model", model?.range ?? "")}
            />
            {/* Model is the only optional field, so it is the only one that needs a way back to
                "any" — the combobox reports selections but never a clear. */}
            <div className="mt-1 text-xs">
              {watchModel ? (
                <button
                  type="button"
                  className="font-medium text-primary-600 hover:underline"
                  onClick={() => setValue("model", "")}
                >
                  {t("Any model")}
                </button>
              ) : (
                <span className="italic text-gray-500">{t("Any model")}</span>
              )}
            </div>
          </div>
        </div>

        <SelectInput
          label={t("Complexity")}
          register={register}
          registerName="complexity"
          options={complexityOptions}
          setValue={setValue}
          watch={watch}
        />

        <TextInput label={t("Notes")} registerName="notes" register={register} placeholder={t("Why this rule exists")} />

        <p className="text-sm text-gray-500">
          {t("Vehicles already in the system keep the complexity they were given. This applies to vehicles created from now on.")}
        </p>

        <ButtonInput
          label={rule ? t("Save changes") : t("Create")}
          isSubmit={true}
          onClick={undefined}
          classes={undefined}
          isLoading={createRule.isLoading || updateRule.isLoading}
        />
      </form>
    </Modal>
  );
}
